import type { DetailLocationDto } from '@/core/data/location/location.interface';
import { gambits } from '@/core/gambits';
import { createLogger } from '@/core/helpers/logger.helper';
import { firestore } from '@/core/network/firebase';
import type { Result } from '@/core/network/http/httpClient.interface';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { ref } from 'vue';

interface LocationFirestore {
  id: string;
  updatedAt?: Timestamp;
  currentSubscribers?: number;
}

type UnsubscribeCallback = () => void;

const logger = createLogger('LocationStore');

export const useLocationStore = defineStore('locationDetail', () => {
  const isLoading = ref<boolean>(false);
  const isError = ref<boolean>(false);
  const isNotFound = ref<boolean>(false);
  const location = ref<DetailLocationDto | null>();

  let unsubscribe: UnsubscribeCallback | null = null;
  let didExist = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let isFetching = false;
  let debouncedFetchTimer: ReturnType<typeof setTimeout> | null = null;

  const POLL_INTERVAL_MS = 5_000;
  const DEBOUNCE_MS = 300;

  const stopPolling = () => {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const startPolling = (id: string) => {
    stopPolling();
    pollTimer = setInterval(() => {
      if (isFetching) return;
      if (location.value?.id === id) {
        logger.info(`Polling refresh for location ${id}`);
        fetch(id);
      } else {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  };

  const clear = async () => {
    stopPolling();

    if (debouncedFetchTimer) {
      clearTimeout(debouncedFetchTimer);
      debouncedFetchTimer = null;
    }

    if (unsubscribe !== null) {
      await unsubscribe();
      unsubscribe = null;
    }

    isLoading.value = false;
    isError.value = false;
    isNotFound.value = false;
    location.value = null;
  };

  const debouncedFetch = (id: string) => {
    if (debouncedFetchTimer) clearTimeout(debouncedFetchTimer);
    debouncedFetchTimer = setTimeout(() => {
      debouncedFetchTimer = null;
      if (location.value?.id === id) {
        fetch(id);
      }
    }, DEBOUNCE_MS);
  };

  const subscribe = (id: string) => {
    if (unsubscribe !== null) {
      unsubscribe();
      unsubscribe = null;
    }

    didExist = false;

    const docRef = doc(firestore, 'locations', id);

    unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        logger.info(`Location ${id} Firestore doc found.`);
        didExist = true;
        const data = docSnap.data() as LocationFirestore;

        if (
          !location.value?.updatedAt ||
          (data.updatedAt && data.updatedAt.toDate() > new Date(location.value.updatedAt))
        ) {
          debouncedFetch(docSnap.id);
        }
      } else if (didExist) {
        logger.info(`Location ${id} Firestore doc deleted.`);
        isNotFound.value = true;
        location.value = null;
      } else {
        logger.warn(`Location ${id} Firestore doc not found. Creating.`);
        gambits.locationService.createFirestoreDoc(id);
      }
    });

    startPolling(id);
  };

  const fetch = async (id: string): Promise<Result<DetailLocationDto> | undefined> => {
    const isRefresh = location.value?.id === id;

    if (!isRefresh) {
      isLoading.value = true;
      isError.value = false;
      isNotFound.value = false;
      location.value = null;
    }

    isFetching = true;
    const result = await gambits.locationService.getLocation(id);
    isFetching = false;
    isLoading.value = false;

    if (result?.data) {
      location.value = result.data;

      if (!isRefresh) {
        subscribe(location.value.id);
      }
    } else if (result?.error?.code === 404) {
      isNotFound.value = true;
      location.value = null;
    } else {
      isError.value = true;
    }

    return result;
  };

  return {
    location,
    isLoading,
    isError,
    isNotFound,
    fetch,
    clear,
  };
});
